<template>
    <div class="container">
        <div v-if="loading">
        </div>
        <div v-else>
            <div v-if="project">
                <div role="button" class="small" @click="goBack()"><i
                        class="fa-solid fa-chevron-left m-2 mt-3"></i>{{ $t('common.back') }}
                </div>
                <h3 class="my-3">{{ project.name }}</h3>

                <div class="accordion shadow mt-3" id="accordionNotes">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': !project.memo4 }" class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseNotes" aria-expanded="false"
                                aria-controls="collapseNotes">
                                {{ $t('browse.technicalInfo') }}
                            </button>
                        </h2>
                        <div id="collapseNotes" class="accordion-collapse collapse" data-bs-parent="#accordionNotes">
                            <div class="accordion-body">
                                <div v-if="project.memo4" class="small text-multiline text-break">
                                    {{ project.memo4 }}
                                </div>
                                <div v-else class="fst-italic small">
                                    {{ $t('browse.noTechnicalInfo') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionLocation">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': !project.address && !project.coordinates }" class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseLocation" aria-expanded="false"
                                aria-controls="collapseLocation">
                                {{ $t('taskDetail.location') }}
                            </button>
                        </h2>
                        <div id="collapseLocation" class="accordion-collapse collapse"
                            data-bs-parent="#accordionLocation">
                            <div class="accordion-body text-multiline">
                                <div v-if="project.address">
                                    <div class="small my-3">{{ $t('taskDetail.address') }}: {{ project.address }}</div>
                                    <div class="row">
                                        <div class="col-auto">
                                            <a :href="googleMapsUrl" :target="mapTarget" rel="noopener"
                                                class="btn btn-primary btn-sm w-100">
                                                Google Maps
                                            </a>
                                        </div>
                                        <div class="col-auto">
                                            <a :href="wazeUrl" :target="mapTarget" rel="noopener"
                                                class="btn btn-primary btn-sm w-100">
                                                Waze
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="project.coordinates">
                                    <div class="small my-3">{{ $t('taskDetail.coordinates') }}: {{ project.coordinates }}</div>
                                    <div class="row">
                                        <div class="col-auto">
                                            <a :href="googleMapsUrlCoordinates" :target="mapTarget" rel="noopener"
                                                class="btn btn-primary btn-sm w-100">
                                                Google Maps
                                            </a>
                                        </div>
                                        <div class="col-auto">
                                            <a :href="wazeUrlCoordinates" :target="mapTarget" rel="noopener"
                                                class="btn btn-primary btn-sm w-100">
                                                Waze
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="!project.address && !project.coordinates" class="small fst-italic">
                                    {{ $t('taskDetail.noLocation') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionFiles">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': project.files.length == 0 }" class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseFiles" aria-expanded="false"
                                aria-controls="collapseFiles">
                                {{ $t('taskDetail.attachments') }}
                            </button>
                        </h2>
                        <div id="collapseFiles" class="accordion-collapse collapse" data-bs-parent="#accordionFiles">
                            <div class="accordion-body">
                                <ul v-if="project.files.length > 0" class="list-unstyled">
                                    <li class="my-2 list-group-item d-flex justify-content-between align-items-start"
                                        v-for="file in project.files" :key="file.id">
                                        <div class="btn btn-light w-100 text-start text-truncate"
                                            @click="requestFile(file)"><i
                                                class="fa-solid fa-paperclip me-2"></i><small>{{ file.filename
                                                }}</small></div>
                                    </li>
                                </ul>
                                <div v-else class="fst-italic small">{{ $t('taskDetail.noAttachments') }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionContact">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': project.customer_contacts.length == 0 }" class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseContact" aria-expanded="false"
                                aria-controls="collapseContact">
                                {{ $t('taskDetail.customerContacts') }}
                            </button>
                        </h2>
                        <div id="collapseContact" class="accordion-collapse collapse"
                            data-bs-parent="#accordionContact">
                            <div class="accordion-body">
                                <div v-if="project.customer_contacts.length > 0">
                                    <h6>{{ $t('browse.customerContactsHeading') }}</h6>
                                    <div v-for="contact in project.customer_contacts" :key="contact.id">
                                        <ul class="list-unstyled">
                                            <li>
                                                <i class="fa-regular fa-user text-danger"></i>
                                                <span class="ps-2"><small>{{ contact.name }}</small></span>
                                            </li>
                                            <li>
                                                <a :href="`tel:${contact.phone}`">
                                                    <i class="fa-solid fa-phone text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                </a>
                                            </li>
                                            <li>
                                                <a :href="`mailto:${contact.email}`">
                                                    <i class="fa-regular fa-envelope text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div v-else class="fst-italic small">{{ $t('taskDetail.noContacts') }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionProjectContact">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': project.contacts.length == 0 }" class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseProjectContact" aria-expanded="false"
                                aria-controls="collapseProjectContact">
                                {{ $t('taskDetail.companyContacts') }}
                            </button>
                        </h2>
                        <div id="collapseProjectContact" class="accordion-collapse collapse"
                            data-bs-parent="#accordionProjectContact">
                            <div class="accordion-body">
                                <div v-if="project.contacts.length > 0">
                                    <div v-for="contact in project.contacts" :key="contact.id">
                                        <h6>{{ contact.title }}</h6>
                                        <ul class="list-unstyled">
                                            <li>
                                                <i class="fa-regular fa-user text-danger"></i>
                                                <span class="ps-2"><small>{{ contact.name }}</small></span>
                                            </li>
                                            <li>
                                                <a :href="`tel:${contact.phone}`">
                                                    <i class="fa-solid fa-phone text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                </a>
                                            </li>
                                            <li>
                                                <a :href="`mailto:${contact.email}`">
                                                    <i class="fa-regular fa-envelope text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div v-else class="fst-italic small">{{ $t('taskDetail.noContacts') }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Four buttons: 2x2 on narrow screens, one row from md up. -->
                <div class="row gy-2 mt-3">
                    <div class="col-6 col-md text-center">
                        <router-link :to="{ name: 'ProjectObjects', params: { projectId: project.id } }">
                            <button class="btn btn-primary">{{ $t('browse.viewDevices') }}</button>
                        </router-link>
                    </div>
                    <div class="col-6 col-md text-center">
                        <router-link :to="{ name: 'ProjectTasks', params: { projectId: project.id } }">
                            <button class="btn btn-primary">{{ $t('browse.viewTasks') }}</button>
                        </router-link>
                    </div>
                    <div class="col-6 col-md text-center">
                        <router-link :to="{ name: 'ProjectMaintenanceLog', params: { projectId: project.id } }">
                            <button class="btn btn-primary">{{ $t('maintenanceLog.title') }}</button>
                        </router-link>
                    </div>
                    <div class="col-6 col-md text-center">
                        <router-link :to="`/projects/${project.id}/new-task`">
                            <button class="btn btn-primary">{{ $t('browse.addNewTask') }}</button>
                        </router-link>
                    </div>
                </div>

            </div>
            <NotFound v-else :item="$t('browse.projectItem')" />
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import { setDocumentTitle } from '@/composables/usePageTitle';
import { downloadBlob } from '@/utils/downloadFile';
import {
    googleMapsAddressUrl, googleMapsCoordinatesUrl,
    wazeAddressUrl, wazeCoordinatesUrl, mapLinkTarget,
} from '@/utils/mapLinks';
import NotFound from '@/components/NotFound.vue';

export default {
    data() {
        return {
            project: null,
            loading: true
        };
    },
    components: {
        NotFound
    },
    methods: {
        async fetchProject() {
            try {
                const projectId = this.$route.params.projectId;
                const response = await axios.get(`/projects/${projectId}`);
                this.project = response.data.data;
                setDocumentTitle(this.project?.name);
            } catch (error) {
                console.error('Error fetching project details:', error);
                // Optionally, handle the error (e.g., set an error message, redirect, etc.)
            } finally {
                this.loading = false;
            }
        },
        async requestFile(file) {
            try {
                const response = await axios({
                    url: `projects/${this.project.id}/files/${file.id}`, // API endpoint to get the file
                    method: 'GET',
                    responseType: 'blob', // Important for files
                });

                downloadBlob(response.data, file.filename);
            } catch (error) {
                this.$root.$refs.globalAlert.showAlert(this.$t('browse.attachmentNotFound'), 'danger');
            }
        },
        goBack() {
            if (this.$route.meta.internalNavigation && window.history.length > 2) {
                this.$router.go(-1);
            } else {
                this.$router.push('/');
            }
        },
    },
    computed: {
        googleMapsUrl() {
            return googleMapsAddressUrl(this.project.address);
        },
        wazeUrl() {
            return wazeAddressUrl(this.project.address);
        },
        googleMapsUrlCoordinates() {
            return googleMapsCoordinatesUrl(this.project.coordinates);
        },
        wazeUrlCoordinates() {
            return wazeCoordinatesUrl(this.project.coordinates);
        },
        mapTarget() {
            return mapLinkTarget();
        },
    },
    mounted() {
        this.fetchProject();
    }
};
</script>